/**
 * CustomerPromptDialog — asked once per session when a send from the home
 * composer is about to auto-create a project and no customer is known yet.
 * Offers the customer autocomplete plus a one-click "testing / internal"
 * escape hatch so internal experiments don't pollute customer analytics
 * with made-up names.
 *
 * Styling matches ConfirmDialog's modal-* classes so it renders native to
 * the app shell in both themes.
 */
import React, { useEffect, useState } from 'react';
import CustomerNameField from './CustomerNameField';

export const TESTING_CUSTOMER = 'Testing / internal';

export default function CustomerPromptDialog({ open, designs = [], initialValue = '', onConfirm, onCancel }) {
  const [value, setValue] = useState('');

  useEffect(() => {
    if (open) setValue(initialValue || '');
  }, [open, initialValue]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onCancel?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;

  const submit = () => {
    const v = value.trim();
    if (v) onConfirm?.(v);
  };

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel?.();
      }}
    >
      <div className="modal-card confirm-dialog" role="dialog" aria-modal="true" aria-label="Customer name">
        <div className="modal-header">
          <h2>Who is this project for?</h2>
        </div>
        <div className="modal-body">
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
            Sending this creates a new project. Enter the customer it&apos;s for, or mark it as
            internal testing.
          </div>
          {/* Enter confirms — without this, typing a name and hitting Enter
              did nothing, which read as the flow being stuck. Read the live
              input value: React state can lag one keystroke behind here. */}
          <div
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              e.preventDefault();
              const live = String(e.target?.value ?? value).trim();
              if (live) onConfirm?.(live);
            }}
          >
            <CustomerNameField
              value={value}
              onChange={setValue}
              required
              designs={designs}
              autoFocus
              placeholder="e.g. Luma"
              label="Customer name"
            />
          </div>
          <button
            type="button"
            className="modal-btn modal-btn-ghost"
            style={{ marginTop: 10, fontSize: 12.5 }}
            onClick={() => onConfirm?.(TESTING_CUSTOMER)}
          >
            This is just testing / internal
          </button>
        </div>
        <div className="modal-footer">
          <button type="button" className="modal-btn modal-btn-ghost" onClick={() => onCancel?.()}>
            Cancel
          </button>
          <button type="button" className="modal-btn modal-btn-primary" onClick={submit} disabled={!value.trim()}>
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}
